import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Tooltip, Table, Input } from "../utils/local-antd";
import { useCallback, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";

import { Spacing } from "../theme/Spacing";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { Text } from "./Text";
import { useTranslation } from "react-i18next";
import { Entry } from "../model/entry/Entry";
import { standardDate } from "../utils/Date";
import { useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import { getEntryLabel } from "../utils/entryUtil";
import { Rounded } from "../theme/Rounded";
import { faX } from "@fortawesome/free-solid-svg-icons/faX";
import { Clickable } from "./Clickable";
import debounce from "lodash/debounce";

const StyledSearch = styled(Input).attrs((props: any) => ({
  "data-testid": props.dataTestId,
  dir: "auto",
}))`
  height: 2.3rem;

  .ant-input-search-btn {
    height: 2.3rem;
  }

  span {
    &:focus-within,
    &:focus {
      box-shadow: none !important;
      border: 1px solid ${({ theme }) => theme.lightgray} !important;
    }
  }
`;

const StyledSelectContainer = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 100%;

  .ant-select {
    height: 2.3rem;
  }

  .ant-select-selector:hover {
    border: 1px solid ${({ theme }) => theme.main} !important;
  }
`;

const InfoSpan = styled.span`
  padding-inline-end: calc(${Spacing.s} / 2);
`;

const SelectedContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background: ${({ theme }) => theme.lightMain};
  color: ${({ theme }) => theme.white};
  width: fit-content;
  gap: ${Spacing.s};
  max-width: 15rem;
`;

export const StyledTable = styled(Table)`
  tbody tr:hover {
    cursor: pointer;
  }
`;

export interface Props<T extends Entry = Entry> {
  fetchOptions: (search: string) => Promise<T[]>;
  onChange: (value: T | null) => void;
  debounceTimeout?: number;
  title?: string;
  defaultOptions?: T[];
  info?: string;
  errorMessage?: string;
  valid?: boolean;
  required?: boolean;
  placeholder?: string;
  isSearchVisible: boolean;
  searchTestId?: string;
  resultItemTestId?: (id: string) => string;
  defaultSelectedEntry?: T | null;
}

/** Notes:
 *  * Must be a child of FormProvider from react-hook-form
 */
export const SearchInputEntries: React.FC<Props> = ({
  fetchOptions,
  debounceTimeout = 800,
  title,
  defaultOptions = [],
  info,
  errorMessage,
  valid = true,
  placeholder,
  isSearchVisible,
  defaultSelectedEntry = null,
  ...props
}) => {
  const [loading, setloading] = useState(false);
  const [options, setOptions] = useState<Entry[]>(() => defaultOptions);
  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
  const [value, setValue] = useState<string>("");
  const ref = useRef<HTMLInputElement>(null);

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);

  const theme = useTheme();
  const { t } = useTranslation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptions = useCallback(
    debounce((value: string) => {
      setOptions([]);
      setloading(true);

      fetchOptions(value)
        .then((newOptions) => {
          setOptions(newOptions || []);
        })
        .finally(() => {
          setloading(false);
        });
    }, 800),
    [fetchOptions, setOptions, setloading],
  );

  const columns = [
    {
      title: t("common.entry"),
      key: "name",
      render: (entry: Entry) => <Text>{getEntryLabel(entry)}</Text>,
    },
    {
      title: t("common.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => {
        return <Text>{standardDate<string>(date || "", user, true)}</Text>;
      },
    },
  ];

  const onRowClick = (record: Entry) => {
    setSelectedEntry(record);
    props.onChange(record);
    setOptions([]);
    ref.current?.blur();
    setValue("");
  };

  useEffect(() => {
    setOptions(defaultOptions);
    if (defaultSelectedEntry) {
      setSelectedEntry(defaultSelectedEntry);
      props.onChange(defaultSelectedEntry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSearchVisible) {
      setSelectedEntry(null);
    }
  }, [isSearchVisible]);

  return (
    <StyledSelectContainer>
      {title ? (
        <Text>
          {info ? (
            <Tooltip title={info}>
              <InfoSpan>
                <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
              </InfoSpan>
            </Tooltip>
          ) : null}
          {title}
          {props.required ? (
            <span style={{ color: theme.danger }}> *</span>
          ) : (
            ""
          )}
        </Text>
      ) : null}

      <StyledSearch
        placeholder={placeholder || t("common.search.min3")}
        value={value}
        onChange={(e) => {
          setValue(e?.target?.value);
          loadOptions(e?.target?.value);
        }}
        allowClear
        onClear={() => {
          setValue("");
          setOptions([]);
        }}
        ref={ref as any}
        // @ts-ignore
        dataTestId={props.searchTestId}
      />

      {selectedEntry ? (
        <SelectedContainer>
          <Text fontSize="xs" color="white">
            {getEntryLabel(selectedEntry)}
          </Text>
          <Clickable
            onClick={() => {
              setSelectedEntry(null);
              props.onChange(null);
            }}
          >
            <AtiraIcon size="sm" icon={faX} />
          </Clickable>
        </SelectedContainer>
      ) : null}

      <StyledTable
        columns={columns}
        dataSource={options?.map((x) => ({ ...x, key: x._id }))}
        size="small"
        showHeader={options?.length > 0}
        locale={{ emptyText: t("common.searchInput.empty") }}
        onRow={(record, rowIndex) => ({
          onClick: () => onRowClick(record as any),
          "data-testid": props.resultItemTestId?.((record as any)._id),
        })}
        loading={loading}
      />

      {valid === false && errorMessage ? (
        <Text color="danger" fontSize="s">
          {errorMessage}
        </Text>
      ) : null}
    </StyledSelectContainer>
  );
};
